import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import NodeActivityEditor from '../../NodeActivityEditor';
import LegacyMedia from '../../LegacyMedia';
import { formatBody } from '../../../utils';
import { ViewSection } from '../styles';
import {
  Questions,
  Question,
  SubmitPanel,
  Alternatives,
  AlternativeWrapper,
  Alternative,
  AlternativeOption,
  InfoPanel,
  Info,
  ProgressPanel,
  QuizInfo,
} from '../../NodeContentPreview/styles';

const QuizContentViewer = ({ type, report, data }) => {
  const [markedAnswers, setMarkedAnswers] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [corrected, setCorrected] = useState(false);

  const questionCount = questions?.length || 0;

  useEffect(() => {
    const newQuestions = data.QuizQuestions || [];
    setQuestions(newQuestions);
    setMarkedAnswers(new Array(newQuestions.length));
  }, []);

  useEffect(() => {
    if (questionCount && report.state === 'accepted') {
      const newAnswers = [];
      const oldAnswers = report?.QuizAnswers || [];
      if (oldAnswers.length) {
        questions.forEach((question) => {
          const answer = oldAnswers.find((answer) => answer.question_id === question.id);
          if (answer) {
            newAnswers.push(answer.alternative_id);
          }
        });
        setMarkedAnswers(newAnswers);
        correctQuiz(newAnswers);
      }
    }
  }, [questions]);

  const getViewForAlternative = (alternative, questionIndex, isQuestionCorrect) => {
    const { id, body, correct } = alternative;

    let className = '';
    if (corrected && !isQuestionCorrect) {
      if (correct) {
        className = 'correct';
      } else if (markedAnswers[questionIndex] === id) {
        className = 'error';
      }
    }

    const alternativeBody = (
      <Alternative className="content-section" dangerouslySetInnerHTML={{ __html: formatBody(body) }} />
    );

    return (
      <>
        {alternative.media_preview ? (
          <div>
            {alternativeBody}
            <LegacyMedia item={alternative} />
          </div>
        ) : (
          alternativeBody
        )}
        <AlternativeOption disabled={true} selected={markedAnswers[questionIndex] === id} className={className} />
      </>
    );
  };

  const getViewForQuestion = (question, questionIndex) => {
    const correctAlternative = question.QuizAlternatives.find((alternative) => alternative.correct);
    const isQuestionCorrect = markedAnswers[questionIndex] === correctAlternative?.id;

    const editorPanel = (
      <Alternatives>
        {question.QuizAlternatives.map((alternative) => (
          <AlternativeWrapper key={alternative.id}>
            {getViewForAlternative(alternative, questionIndex, isQuestionCorrect)}
          </AlternativeWrapper>
        ))}
      </Alternatives>
    );

    return (
      <NodeActivityEditor
        type={type}
        index={questionIndex}
        legacyContent={question}
        headerText={formatBody(question.body)}
        editorPanel={editorPanel}
        className={corrected && !isQuestionCorrect ? 'error' : ''}
      />
    );
  };

  const mappedQuestions = questions.map((question, index) => (
    <Question id={`question_${index}`} key={question.id}>
      {getViewForQuestion(question, index)}
    </Question>
  ));

  const correctQuiz = (answers) => {
    let correct = 0;
    questions.forEach((question, index) => {
      const correctAnswer = question.QuizAlternatives.find((alternative) => alternative.correct);
      if (correctAnswer?.id === answers[index]) {
        correct++;
      }
    });
    setCorrectAnswers(correct);
    setCorrected(true);
  };

  return (
    <>
      <ViewSection>
        <Questions>{mappedQuestions}</Questions>
        <QuizInfo>{i18n.__(data.redoable ? 'correlation_redoable_desc' : 'correlation_not_redoable_desc')}</QuizInfo>
      </ViewSection>
      <SubmitPanel>
        {corrected ? (
          <ProgressPanel>
            <Info>{i18n.__('quiz_correcting_result', correctAnswers, questionCount)}</Info>
          </ProgressPanel>
        ) : (
          <InfoPanel>
            <Info>{i18n.__('quiz_question_count', questionCount)}</Info>
          </InfoPanel>
        )}
      </SubmitPanel>
    </>
  );
};

export default QuizContentViewer;

QuizContentViewer.propTypes = {
  type: PropTypes.string.isRequired,
  report: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
};
